import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Crown, 
  Check, 
  Star, 
  Sparkles, 
  BarChart3, 
  Zap, 
  Shield,
  Loader2,
  AlertCircle
} from 'lucide-react';

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const { user, api, refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, statusRes] = await Promise.all([
        api.get('/subscription/plans'),
        api.get('/subscription/status')
      ]);
      setPlans(plansRes.data);
      setSubscription(statusRes.data);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    setCheckoutLoading(true);
    try {
      const response = await api.post('/subscription/checkout', {
        plan_id: planId,
        origin_url: window.location.origin
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de paiement');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleCancel = async () => {
    setCancelLoading(true);
    try {
      await api.post('/subscription/cancel');
      toast.success('Abonnement annulé');
      setCancelDialog(false);
      fetchData();
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setCancelLoading(false);
    }
  };

  const getPlanIcon = (planId) => {
    if (planId === 'premium_yearly') return <Crown className="h-8 w-8 text-yellow-500" />;
    return <Star className="h-8 w-8 text-primary" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen tropic-bg flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen tropic-bg">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b">
        <div className="container mx-auto max-w-4xl px-4 py-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">Abonnement Premium</h1>
              <p className="text-sm text-muted-foreground">Débloquez tout le potentiel de Bourbon</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto max-w-4xl px-4 py-6 space-y-6">
        {/* Current Subscription Status */}
        {subscription?.is_premium && (
          <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="h-14 w-14 rounded-full bg-primary/20 flex items-center justify-center">
                    <Crown className="h-7 w-7 text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-bold">Premium Actif</h2>
                      <Badge className="bg-primary/20 text-primary">
                        {subscription.plan_name}
                      </Badge>
                    </div>
                    <p className="text-muted-foreground">
                      {subscription.cancel_at_period_end 
                        ? `Se termine le ${new Date(subscription.current_period_end).toLocaleDateString('fr-FR')}`
                        : `Renouvellement le ${new Date(subscription.current_period_end).toLocaleDateString('fr-FR')}`
                      }
                    </p>
                  </div>
                </div>
                {!subscription.cancel_at_period_end && (
                  <Button 
                    variant="outline" 
                    onClick={() => setCancelDialog(true)}
                    className="text-destructive border-destructive/20 hover:bg-destructive/10"
                  >
                    Annuler
                  </Button>
                )}
              </div>
              {subscription.cancel_at_period_end && (
                <div className="mt-4 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-yellow-600" />
                  <p className="text-sm text-yellow-700 dark:text-yellow-400">
                    Votre abonnement ne sera pas renouvelé. Vous gardez l'accès Premium jusqu'à la fin de la période.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Premium Benefits */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: Zap, label: 'Posts illimités', desc: 'Publiez sans limite' },
            { icon: BarChart3, label: 'Stats avancées', desc: 'Analysez votre audience' },
            { icon: Shield, label: 'Badge vérifié', desc: 'Crédibilité renforcée' },
            { icon: Sparkles, label: 'Boost gratuit', desc: '1 boost/mois inclus' }
          ].map((benefit, i) => (
            <Card key={i} className="text-center p-4">
              <benefit.icon className="h-8 w-8 mx-auto mb-2 text-primary" />
              <p className="font-medium text-sm">{benefit.label}</p>
              <p className="text-xs text-muted-foreground">{benefit.desc}</p>
            </Card>
          ))}
        </div>

        {/* Pricing Plans */}
        {!subscription?.is_premium && (
          <div className="grid md:grid-cols-2 gap-6">
            {plans.map((plan) => (
              <Card 
                key={plan.id} 
                className={`relative overflow-hidden ${
                  plan.id === 'premium_yearly' 
                    ? 'border-2 border-primary shadow-lg' 
                    : ''
                }`}
              >
                {plan.id === 'premium_yearly' && (
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-primary text-primary-foreground">
                      Économisez 17%
                    </Badge>
                  </div>
                )}
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    {getPlanIcon(plan.id)}
                    <div>
                      <CardTitle>{plan.name}</CardTitle>
                      <CardDescription>
                        {plan.interval === 'year' ? 'Facturé annuellement' : 'Facturé mensuellement'}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pb-4">
                  <div className="mb-4">
                    <span className="text-4xl font-bold">{plan.price}€</span>
                    <span className="text-muted-foreground">
                      /{plan.interval === 'year' ? 'an' : 'mois'}
                    </span>
                  </div>
                  <ul className="space-y-2">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button 
                    className="w-full"
                    variant={plan.id === 'premium_yearly' ? 'default' : 'outline'}
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={checkoutLoading}
                    data-testid={`subscribe-${plan.id}`}
                  >
                    {checkoutLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Crown className="h-4 w-4 mr-2" />
                        S'abonner
                      </>
                    )}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}

        {/* FAQ */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Questions fréquentes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-medium">Puis-je annuler à tout moment ?</h4>
              <p className="text-sm text-muted-foreground">
                Oui, vous pouvez annuler votre abonnement à tout moment. Vous conserverez l'accès Premium jusqu'à la fin de votre période de facturation.
              </p>
            </div>
            <div>
              <h4 className="font-medium">Comment fonctionne le boost gratuit ?</h4>
              <p className="text-sm text-muted-foreground">
                Chaque mois, vous recevez un crédit pour booster un post pendant 24h. Les abonnés annuels reçoivent 3 boosts par mois.
              </p>
            </div>
            <div>
              <h4 className="font-medium">Quelles statistiques sont disponibles ?</h4>
              <p className="text-sm text-muted-foreground">
                Taux d'engagement, répartition par commune et catégorie, meilleur post, évolution des followers, et plus encore.
              </p>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialog} onOpenChange={setCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Annuler votre abonnement ?</DialogTitle>
            <DialogDescription>
              Vous perdrez l'accès aux fonctionnalités Premium à la fin de votre période de facturation actuelle.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Votre accès reste actif jusqu'au{' '}
              <strong>
                {subscription?.current_period_end 
                  ? new Date(subscription.current_period_end).toLocaleDateString('fr-FR')
                  : 'la fin de la période'}
              </strong>.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialog(false)}>
              Garder Premium
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCancel}
              disabled={cancelLoading}
            >
              {cancelLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirmer l\'annulation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubscriptionPage;
